import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, X, ArrowRight, Sparkles, Zap, Clock, Target } from 'lucide-react';
import { PollarLogoSVG } from '@/constants/logo';
import { trackPageView, trackClick, EventTypes } from '@/utils/analytics';

const MiroAlternative = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackPageView('miro-alternative');
  }, []);

  const handleCTA = (location) => {
    trackClick(`miro-alternative-cta-${location}`, window.location.href);
    navigate('/login?signup=true');
  };

  const comparisonRows = [
    {
      feature: 'Focus',
      standard: 'Visual Collaboration & Drawing',
      pollar: 'Decision-Making & Action',
      standardIcon: false,
      pollarIcon: true
    },
    {
      feature: 'Organization',
      standard: 'Manual (You drag and drop sticky notes)',
      pollar: 'Automated (AI clusters by theme)',
      standardIcon: false,
      pollarIcon: true
    },
    {
      feature: 'Post-Meeting',
      standard: 'A "screenshot" of a messy board',
      pollar: 'A structured, prioritized roadmap',
      standardIcon: false,
      pollarIcon: true
    },
    {
      feature: 'Learning Curve',
      standard: 'High (Requires "Board Mastery")',
      pollar: 'Zero (Invite-and-go)',
      standardIcon: false,
      pollarIcon: true
    }
  ];

  return (
    <>
      <Helmet>
        <title>Best Miro Alternative for Fast Brainstorming | Pollar</title>
        <meta 
          name="description" 
          content="Tired of messy whiteboards? Pollar is the AI-driven Miro alternative that turns brainstorming sessions into organized action items automatically." 
        />
        <meta property="og:title" content="Best Miro Alternative for Fast Brainstorming | Pollar" />
        <meta 
          property="og:description" 
          content="Tired of messy whiteboards? Pollar is the AI-driven Miro alternative that turns brainstorming sessions into organized action items automatically." 
        />
        <link rel="canonical" href={`${process.env.REACT_APP_BACKEND_URL}/alternatives/miro-alternative-pollar`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => navigate('/')}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <PollarLogoSVG className="w-10 h-10" />
                <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Pollar
                </span>
              </button>
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => handleCTA('nav')}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                >
                  Get Started Free
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Section A: Hero */}
        <section className="pt-20 pb-16 px-6">
          <div className="container mx-auto max-w-5xl text-center">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              The Smarter Alternative to Miro & Mural
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Brainstorming Without the{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                "Manual Cleanup"
              </span>
            </h1>

            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
                From Sticky Notes to Strategy, Instantly.
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Infinite whiteboards are great for drawing, but terrible for deciding. Pollar replaces the manual "clustering" phase with AI, so you spend less time moving boxes and more time making moves.
              </p>
            </div>

            <Button 
              size="lg"
              onClick={() => handleCTA('hero')}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-lg px-8 py-6 h-auto"
            >
              Start Your Faster Workshop
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>

            <p className="text-sm text-gray-500 mt-4">
              No training needed • AI does the work • Results in real-time
            </p>

            {/* Quick Stats */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Clock className="w-6 h-6 text-indigo-600" />
                  <span className="text-3xl font-bold text-gray-900">2hrs</span>
                </div>
                <p className="text-sm text-gray-600">Saved per workshop</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Zap className="w-6 h-6 text-indigo-600" />
                  <span className="text-3xl font-bold text-gray-900">0min</span>
                </div>
                <p className="text-sm text-gray-600">Setup time required</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Target className="w-6 h-6 text-indigo-600" />
                  <span className="text-3xl font-bold text-gray-900">100%</span>
                </div>
                <p className="text-sm text-gray-600">Auto-organized</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section B: Comparison Table */}
        <section className="py-16 px-6 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">What Makes Pollar Different?</h2>
              <p className="text-xl text-gray-600">
                Same brainstorming power, minus the post-meeting cleanup
              </p>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-6 px-6 text-left bg-gray-50 font-bold text-lg">
                      Feature
                    </th>
                    <th className="py-6 px-6 text-center bg-gray-100 text-gray-500 font-semibold">
                      Infinite Whiteboards
                      <div className="text-sm font-normal mt-1">(Miro/Mural)</div>
                    </th>
                    <th className="py-6 px-6 text-center bg-gradient-to-r from-indigo-50 to-purple-50 font-bold text-lg">
                      <div className="flex items-center justify-center gap-2">
                        <Sparkles className="w-5 h-5 text-indigo-600" />
                        Pollar Ideation
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-6 px-6 font-semibold text-gray-900">
                        {row.feature}
                      </td>
                      <td className="py-6 px-6 bg-gray-50">
                        <div className="flex items-center justify-center gap-3 text-gray-600">
                          <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <span className="text-center">{row.standard}</span>
                        </div>
                      </td>
                      <td className="py-6 px-6 bg-indigo-50/30">
                        <div className="flex items-center justify-center gap-3 text-gray-900 font-medium">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-center">{row.pollar}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-6">
              {comparisonRows.map((row, index) => (
                <Card key={index} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-4 text-gray-900">{row.feature}</h3>
                    
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-sm font-semibold text-gray-500 mb-1">Infinite Whiteboards</div>
                            <div className="text-gray-600">{row.standard}</div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-sm font-semibold text-indigo-700 mb-1">Pollar</div>
                            <div className="text-gray-900 font-medium">{row.pollar}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Section C: The Tangible Difference */}
        <section className="py-16 px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">
                Don't Let Your Best Ideas Die on a Whiteboard
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                The biggest problem with visual boards is the "Post-Workshop Fatigue." Someone has to spend hours typing up the results. With Pollar, the AI does the synthesis in real-time. When the meeting ends, your report is already done.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* The Problem */}
              <Card className="border-2 border-red-200 relative overflow-hidden">
                <div className="absolute top-4 right-4 bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
                  The Problem
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-gray-500">Infinite Whiteboards</h3>
                  
                  {/* Messy Board Visual */}
                  <div className="bg-gray-100 rounded-lg p-6 min-h-[320px] relative overflow-hidden">
                    {/* Scattered sticky notes */}
                    <div className="absolute top-2 left-2 bg-yellow-200 p-2 rounded shadow-sm text-xs transform -rotate-3">
                      Feature idea
                    </div>
                    <div className="absolute top-12 left-20 bg-pink-200 p-2 rounded shadow-sm text-xs transform rotate-2">
                      Bug fix
                    </div>
                    <div className="absolute top-2 right-16 bg-blue-200 p-2 rounded shadow-sm text-xs transform rotate-6">
                      User feedback
                    </div>
                    <div className="absolute top-20 right-4 bg-green-200 p-2 rounded shadow-sm text-xs transform -rotate-6">
                      Improvement
                    </div>
                    <div className="absolute bottom-24 left-8 bg-yellow-200 p-2 rounded shadow-sm text-xs transform rotate-3">
                      New feature
                    </div>
                    <div className="absolute bottom-24 right-12 bg-pink-200 p-2 rounded shadow-sm text-xs transform -rotate-2">
                      Question
                    </div>
                    <div className="absolute bottom-8 left-24 bg-blue-200 p-2 rounded shadow-sm text-xs transform rotate-4">
                      Blocker
                    </div>
                    <div className="absolute bottom-4 right-20 bg-green-200 p-2 rounded shadow-sm text-xs transform -rotate-5">
                      Action item
                    </div>
                    <div className="absolute top-32 left-32 bg-purple-200 p-2 rounded shadow-sm text-xs transform rotate-1">
                      Discussion
                    </div>
                    <div className="absolute top-40 right-28 bg-orange-200 p-2 rounded shadow-sm text-xs transform -rotate-4">
                      Follow-up
                    </div>
                    
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 text-center">
                        <p className="text-gray-600 font-medium">47 sticky notes</p>
                        <p className="text-sm text-gray-500">scattered everywhere</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Someone has to manually cluster all ideas</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>2-3 hours of post-meeting cleanup</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Final report written days later</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Momentum lost, decisions delayed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* The Solution */}
              <Card className="border-2 border-green-500 relative overflow-hidden shadow-xl">
                <div className="absolute top-4 right-4 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />
                  The Solution
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    Pollar Ideation
                  </h3>
                  
                  {/* Organized Output */}
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 min-h-[320px]">
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-100">
                        <div className="flex items-start gap-3">
                          <div className="bg-indigo-600 rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-sm">1</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900 mb-1">Product Features</h4>
                            <p className="text-sm text-gray-600 mb-2">12 ideas automatically grouped</p>
                            <div className="flex flex-wrap gap-1">
                              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">High Priority</span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Q1 2025</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-100">
                        <div className="flex items-start gap-3">
                          <div className="bg-indigo-600 rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-sm">2</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900 mb-1">Technical Improvements</h4>
                            <p className="text-sm text-gray-600 mb-2">8 suggestions clustered</p>
                            <div className="flex flex-wrap gap-1">
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Medium Priority</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-100">
                        <div className="flex items-start gap-3">
                          <div className="bg-indigo-600 rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-sm">3</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900 mb-1">User Experience</h4>
                            <p className="text-sm text-gray-600">6 related items</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">AI clusters ideas in real-time</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">Report ready when meeting ends</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">Priorities identified automatically</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">Team moves forward immediately</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Additional Context */}
            <div className="mt-12 max-w-4xl mx-auto">
              <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 border-0">
                <CardContent className="p-8 text-center text-white">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-90" />
                  <h3 className="text-2xl font-bold mb-3">
                    Save 2+ Hours Per Workshop
                  </h3>
                  <p className="text-lg text-indigo-100">
                    The average facilitator spends 2-3 hours after each workshop organizing notes, 
                    clustering themes, and writing up results. Pollar does this in seconds while you're 
                    still in the meeting.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Use Case Section */}
        <section className="py-16 px-6 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Perfect For Teams Who Need Speed</h2>
              <p className="text-xl text-gray-600">
                When the quality of your decisions matters more than the aesthetics of your canvas
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-gray-200 hover:border-indigo-300 transition-colors">
                <CardContent className="p-6">
                  <div className="bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <Zap className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Product Teams</h3>
                  <p className="text-gray-600">
                    Turn sprint planning chaos into prioritized roadmaps without the post-meeting grind.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-indigo-300 transition-colors">
                <CardContent className="p-6">
                  <div className="bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <Target className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Consultants</h3>
                  <p className="text-gray-600">
                    Deliver client workshops with instant, professional reports that justify your value.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-indigo-300 transition-colors">
                <CardContent className="p-6">
                  <div className="bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Remote Teams</h3>
                  <p className="text-gray-600">
                    Make every async brainstorming session count with AI-powered synthesis.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Section D: Final CTA */}
        <section className="py-20 px-6 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Save 2 Hours on Your Next Workshop
            </h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
              Stop spending your evenings organizing sticky notes. Let AI handle the synthesis 
              so you can focus on what matters: making decisions and taking action.
            </p>
            <Button 
              size="lg"
              onClick={() => handleCTA('final')}
              className="bg-white text-indigo-600 hover:bg-gray-100 text-lg px-8 py-6 h-auto font-semibold"
            >
              Try Pollar for Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-indigo-100">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>No training required</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>Free plan available</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>Results in real-time</span>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-400 py-12 px-6">
          <div className="container mx-auto max-w-6xl text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <PollarLogoSVG className="w-8 h-8" />
              <span className="text-xl font-bold text-white">Pollar</span>
            </div>
            <p className="mb-4">
              Brainstorming tool that does the synthesis for you
            </p>
            <div className="flex items-center justify-center gap-6 text-sm">
              <button onClick={() => navigate('/')} className="hover:text-white transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/alternatives/mentimeter-vs-pollar')} className="hover:text-white transition-colors">
                vs Mentimeter
              </button>
              <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">
                Sign In
              </button>
              <button onClick={() => handleCTA('footer')} className="hover:text-white transition-colors">
                Get Started
              </button>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800 text-sm">
              © {new Date().getFullYear()} Pollar. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default MiroAlternative;
